import React, { useState, useEffect, useMemo, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Grid,
  Paper,
  Stack,
  ThemeProvider,
  createTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import DescriptionIcon from "@mui/icons-material/Description";
import PeopleIcon from "@mui/icons-material/People";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";
import { commissionsApi } from "../../Components/commissionsApi";
import { useTheme } from "../../hooks/useTheme";
import { createMuiThemeFromMode } from "../../theme/theme";
import { RoleContext } from "../../Contexts/RoleContext";
import { CurrencyContext } from "../../Contexts/CurrencyContext";
import styles from "../AllEnquiries.module.scss";
import dashboardStyles from "../dashboard/Dashboard.module.scss";

const baseThemeOptions = {
  palette: { secondary: { main: "#f5365c" }, success: { main: "#2dce89" }, warning: { main: "#fb6340" }, info: { main: "#11cdef" } },
  typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", button: { textTransform: "none", fontWeight: 500 } },
  shape: { borderRadius: 12 },
};

const SOURCE_LABELS = {
  total_business_revenue: "Total Business Revenue",
  total_conversions: "Total Conversions",
  conversions_by_program: "Conversions by Program",
  conversions_by_course: "Conversions by Course",
};

const TRIGGER_LABELS = {
  conversion_created: "Conversion created",
  revenue_recorded: "Revenue recorded",
  registration_completed: "Registration completed",
  payment_received: "Payment received",
};

function CommissionProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useContext(RoleContext);
  const { formatCurrency } = useContext(CurrencyContext);
  const { themeMode } = useTheme();
  const theme = useMemo(() => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)), [themeMode]);

  const [structure, setStructure] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [records, setRecords] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignUserId, setAssignUserId] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(null);

  const canManage = role === "admin" || role === "Manager";

  const fetchStructure = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await commissionsApi.getById(id);
      setStructure(res.structure);
    } catch (err) {
      toast.error(err?.message || "Failed to load structure");
      setStructure(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async () => {
    if (!id) return;
    try {
      const res = await commissionsApi.assignments(id);
      setAssignments(res.assignments || []);
    } catch {
      setAssignments([]);
    }
  };

  const fetchRecords = async () => {
    if (!id) return;
    try {
      const res = await commissionsApi.records(id);
      setRecords(res.records || []);
    } catch {
      setRecords([]);
    }
  };

  const fetchAnalytics = async () => {
    if (!id) return;
    try {
      const res = await commissionsApi.analytics(id);
      setAnalytics(res.analytics || null);
    } catch {
      setAnalytics(null);
    }
  };

  useEffect(() => {
    fetchStructure();
  }, [id]);

  useEffect(() => {
    if (id && tab === 1) fetchAssignments();
  }, [id, tab]);
  useEffect(() => {
    if (id && tab === 2) fetchRecords();
  }, [id, tab]);
  useEffect(() => {
    if (id && tab === 3) fetchAnalytics();
  }, [id, tab]);

  useEffect(() => {
    if (canManage) {
      commissionsApi.employees().then((r) => setEmployees(r.employees || [])).catch(() => setEmployees([]));
    }
  }, [canManage]);

  const handleAssign = async () => {
    if (!assignUserId) return;
    setAssignLoading(true);
    try {
      await commissionsApi.assignUser(id, Number(assignUserId));
      toast.success("User assigned.");
      setAssignOpen(false);
      setAssignUserId("");
      fetchAssignments();
    } catch (err) {
      toast.error(err?.message || "Failed to assign");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleRemoveAssignment = async (userId) => {
    try {
      await commissionsApi.removeAssignment(id, userId);
      toast.success("User removed.");
      fetchAssignments();
    } catch (err) {
      toast.error(err?.message || "Failed to remove");
    }
  };

  const handleUpdateRecordStatus = async (recordId, status) => {
    setStatusUpdateLoading(recordId);
    try {
      await commissionsApi.updateRecordStatus(id, recordId, status);
      toast.success("Status updated.");
      fetchRecords();
    } catch (err) {
      toast.error(err?.message || "Failed to update");
    } finally {
      setStatusUpdateLoading(null);
    }
  };

  if (loading && !structure) {
    return (
      <ThemeProvider theme={theme}>
        <Box className={`${styles.root} ${dashboardStyles.dashboardContent}`}>
          <Paper elevation={0} className={`${styles.contentPaper} ${dashboardStyles.contentPaper}`} sx={{ p: 3 }}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
          </Paper>
        </Box>
      </ThemeProvider>
    );
  }

  if (!structure) {
    return (
      <ThemeProvider theme={theme}>
        <Box className={`${styles.root} ${dashboardStyles.dashboardContent}`}>
          <Paper elevation={0} className={`${styles.contentPaper} ${dashboardStyles.contentPaper}`} sx={{ p: 3 }}>
            <Typography color="text.secondary">Commission structure not found.</Typography>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/commissions")} sx={{ mt: 2 }}>Back to Commissions</Button>
          </Paper>
        </Box>
      </ThemeProvider>
    );
  }

  const s = structure;

  return (
    <ThemeProvider theme={theme}>
      <Box className={`${styles.root} ${dashboardStyles.dashboardContent}`}>
        <Paper elevation={0} className={`${styles.contentPaper} ${dashboardStyles.contentPaper}`} sx={{ p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/commissions")} sx={{ mb: 1 }}>Back to Commissions</Button>
          </Box>

              <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{ bgcolor: "primary.main", color: "primary.contrastText", p: 1.5, borderRadius: 2 }}>
                        <AccountBalanceIcon fontSize="large" />
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight={600}>{s.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {s.timeframe ? String(s.timeframe).charAt(0).toUpperCase() + s.timeframe.slice(1) : ""} · {TRIGGER_LABELS[s.trigger_event] || s.trigger_event}
                        </Typography>
                        <Chip label={s.active ? "Active" : "Inactive"} color={s.active ? "success" : "default"} size="small" sx={{ mt: 0.5 }} />
                      </Box>
                    </Box>
                    {canManage && (
                      <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/commissions/${id}/edit`)}>Edit</Button>
                    )}
                  </Box>
                </CardContent>
              </Card>

              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tab icon={<DescriptionIcon />} iconPosition="start" label="Details" />
                <Tab icon={<PeopleIcon />} iconPosition="start" label="Assigned Users" />
                <Tab icon={<ReceiptIcon />} iconPosition="start" label="Commission Breakdown" />
                <Tab icon={<AnalyticsIcon />} iconPosition="start" label="Commission Analysis" />
              </Tabs>

              {tab === 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>{s.description || "—"}</Typography>
                    <Typography variant="subtitle2" color="text.secondary">Timeframe</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>{s.timeframe ? String(s.timeframe).charAt(0).toUpperCase() + s.timeframe.slice(1) : "—"}</Typography>
                    <Typography variant="subtitle2" color="text.secondary">Sources</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>{(s.rules || []).map((r) => SOURCE_LABELS[r.source] || r.source).join(", ") || "—"}</Typography>
                    <Typography variant="subtitle2" color="text.secondary">Brackets (per rule)</Typography>
                    {(s.rules || []).map((r, i) => (
                      <Box key={i} sx={{ mb: 2 }}>
                        <Typography variant="body2">{SOURCE_LABELS[r.source] || r.source}: {(r.brackets || []).map((b) => `${b.min_value}–${b.max_value ?? "∞"} → ${r.rule_type === "percentage" ? `${b.value}%` : formatCurrency(b.value)}`).join(", ")}</Typography>
                      </Box>
                    ))}
                    <Typography variant="subtitle2" color="text.secondary">Threshold</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>{s.minimum_threshold != null ? s.minimum_threshold : "—"}</Typography>
                    <Typography variant="subtitle2" color="text.secondary">Cap</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>{s.commission_cap != null ? formatCurrency(s.commission_cap) : "—"}</Typography>
                    <Typography variant="subtitle2" color="text.secondary">Trigger Event</Typography>
                    <Typography variant="body2">{TRIGGER_LABELS[s.trigger_event] || s.trigger_event}</Typography>
                  </CardContent>
                </Card>
              )}

              {tab === 1 && (
                <Card>
                  <CardContent>
                    {canManage && (
                      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setAssignOpen(true)}>Assign user</Button>
                      </Stack>
                    )}
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Employee</TableCell>
                          <TableCell>Progress</TableCell>
                          <TableCell>Commission Earned</TableCell>
                          <TableCell>Status</TableCell>
                          {canManage && <TableCell>Actions</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {assignments.length === 0 && (
                          <TableRow><TableCell colSpan={5} align="center">No users assigned.</TableCell></TableRow>
                        )}
                        {assignments.map((a) => (
                          <TableRow key={a.id}>
                            <TableCell>{a.display_name}</TableCell>
                            <TableCell>
                              {(a.progress?.rules || []).length > 0 ? (
                                <Typography variant="body2">{(a.progress.rules || []).map((r) => `${r.metric_value} (${r.source})`).join(", ")}</Typography>
                              ) : "—"}
                            </TableCell>
                            <TableCell>{formatCurrency(Number(a.commission_earned || 0))}</TableCell>
                            <TableCell><Chip label="Active" size="small" color="success" variant="outlined" /></TableCell>
                            {canManage && (
                              <TableCell>
                                <IconButton size="small" color="error" onClick={() => handleRemoveAssignment(a.user_id)}><PersonRemoveIcon /></IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {tab === 2 && (
                <Card>
                  <CardContent>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>Metric</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Date</TableCell>
                          {canManage && <TableCell>Actions</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {records.length === 0 && (
                          <TableRow><TableCell colSpan={6} align="center">No commission records yet.</TableCell></TableRow>
                        )}
                        {records.map((r) => (
                          <TableRow key={r.id}>
                            <TableCell>{r.user_display_name}</TableCell>
                            <TableCell>{r.metric_value}</TableCell>
                            <TableCell>{formatCurrency(Number(r.commission_amount))}</TableCell>
                            <TableCell><Chip label={r.status} size="small" variant="outlined" /></TableCell>
                            <TableCell>{r.calculated_at ? new Date(r.calculated_at).toLocaleString() : "—"}</TableCell>
                            {canManage && r.status === "pending" && (
                              <TableCell>
                                <Stack direction="row" spacing={0.5}>
                                  <Button size="small" onClick={() => handleUpdateRecordStatus(r.id, "approved")} disabled={statusUpdateLoading === r.id}>Approve</Button>
                                  <Button size="small" color="error" onClick={() => handleUpdateRecordStatus(r.id, "cancelled")} disabled={statusUpdateLoading === r.id}>Cancel</Button>
                                </Stack>
                              </TableCell>
                            )}
                            {canManage && r.status === "approved" && (
                              <TableCell>
                                <Button size="small" onClick={() => handleUpdateRecordStatus(r.id, "paid")} disabled={statusUpdateLoading === r.id}>Mark Paid</Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {tab === 3 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">Revenue Generated</Typography>
                        <Typography variant="h4">{formatCurrency(Number(analytics?.revenue_generated || 0))}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">Total Commission Paid</Typography>
                        <Typography variant="h4">{formatCurrency(Number(analytics?.total_commission_paid || 0))}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">Revenue to Commission Ratio (ROI)</Typography>
                        <Typography variant="h4">{Number(analytics?.revenue_to_commission_ratio || 0).toFixed(1)}x</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
        </Paper>
      </Box>

      <Dialog open={assignOpen} onClose={() => !assignLoading && setAssignOpen(false)}>
          <DialogTitle>Assign user</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 1, minWidth: 200 }}>
              <InputLabel>User</InputLabel>
              <Select value={assignUserId} label="User" onChange={(e) => setAssignUserId(e.target.value)}>
                {employees.filter((e) => !assignments.some((a) => a.user_id === e.id)).map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>{emp.display_name || emp.username}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignOpen(false)} disabled={assignLoading}>Cancel</Button>
            <Button variant="contained" onClick={handleAssign} disabled={assignLoading || !assignUserId}>Assign</Button>
          </DialogActions>
        </Dialog>
    </ThemeProvider>
  );
}

export default CommissionProfile;
